import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCity } from "../context/CityContext";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CalendarIcon, ArrowLeft, ShieldCheck, Clock, MapPin, AlertCircle, CreditCard, QrCode, User, Phone, MessageSquareText } from "lucide-react";
import { SERVICE_ICONS, DefaultIcon } from "../lib/icons";
import { waLink } from "../lib/wa";
import { format } from "date-fns";
import SimpleLocationInput from "../components/SimpleLocationInput";
import UpiPaymentModal from "../components/UpiPaymentModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function bookLabel({ submitting, city, svc, method }) {
  if (svc?.quote_only) return "Get WhatsApp quote";
  if (submitting) return "Creating booking…";
  if (!city) return "Choose city to continue";
  if (city.status !== "live") return `Join waitlist for ${city.name}`;
  return method === "upi" ? "Book & Pay via UPI" : "Book & Pay with Card";
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user, token, authFetch } = useAuth();
  const { city, setPickerOpen } = useCity();
  const [svc, setSvc] = useState(null);
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("10:00");
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Contact prefilled from account (editable)
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  // Simple location state — plain text
  const [pickup, setPickup] = useState({ address: "", landmark: "" });
  const [drop, setDrop] = useState({ address: "", landmark: "" });
  // Payment
  const [payMethod, setPayMethod] = useState("upi");
  const [upiBooking, setUpiBooking] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services/${slug}`).then((r) => setSvc(r.data)).catch(() => nav("/services"));
  }, [slug, nav]);

  // Prefill pickup from ?pickup=&landmark= (used by Coolie Finder → station click)
  useEffect(() => {
    const p = sp.get("pickup");
    const l = sp.get("landmark");
    if (p || l) setPickup((prev) => ({ address: p || prev.address, landmark: l || prev.landmark }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString()]);

  useEffect(() => {
    if (user) {
      setContactName((cn) => cn || user.name || "");
      setContactPhone((cp) => cp || user.phone || "");
    }
  }, [user]);

  if (!svc) return <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">Loading…</div>;

  const Icon = SERVICE_ICONS[svc.icon] || DefaultIcon;
  const perHour = svc.unit.startsWith("per 60");
  const total = svc.price_inr * (perHour ? hours : 1);
  const platformFee = Math.round(total * 0.33);
  const partnerEarn = total - platformFee;
  const locMode = svc.location_mode || "single";

  const composedAddress = () => {
    const parts = [];
    if (pickup.address) parts.push(pickup.address);
    if (pickup.landmark) parts.push(`(near ${pickup.landmark})`);
    if (locMode === "route" && drop.address) parts.push(`→ ${drop.address}${drop.landmark ? ` (near ${drop.landmark})` : ""}`);
    return parts.join(" ").trim();
  };

  const buildPayload = () => {
    const scheduled = new Date(date);
    const [h, m] = time.split(":").map(Number);
    scheduled.setHours(h, m, 0, 0);
    const payload = {
      service_slug: svc.slug,
      hours: perHour ? hours : 1,
      address: composedAddress() || pickup.address || "",
      scheduled_at: scheduled.toISOString(),
      notes: notes + (city ? ` [City: ${city.name}]` : ""),
      contact_name: contactName || null,
      contact_phone: contactPhone || null,
    };
    if (locMode !== "none") {
      payload.pickup_address = pickup.address || null;
      payload.pickup_landmark = pickup.landmark || null;
    }
    if (locMode === "route") {
      payload.drop_address = drop.address || null;
      payload.drop_landmark = drop.landmark || null;
    }
    return payload;
  };

  const whatsAppText = () => {
    const lines = [
      `Hi HeylpMate! I'd like to book *${svc.name}*.`,
      contactName && `Name: ${contactName}`,
      contactPhone && `Phone: ${contactPhone}`,
      pickup.address && `Pickup: ${pickup.address}${pickup.landmark ? ` (near ${pickup.landmark})` : ""}`,
      locMode === "route" && drop.address && `Drop: ${drop.address}${drop.landmark ? ` (near ${drop.landmark})` : ""}`,
      `When: ${format(date, "PPP")} ${time}`,
      notes && `Notes: ${notes}`,
      `City: ${city?.name || "TBD"}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const handleWhatsApp = () => window.open(waLink(whatsAppText()), "_blank");

  const handleBook = async () => {
    if (svc.quote_only) return handleWhatsApp();
    if (!city) { toast.info("Please choose your city first"); setPickerOpen(true); return; }
    if (city.status !== "live") {
      toast.info(`${city.name} launches on ${city.launch}. Join the waitlist via WhatsApp.`);
      window.open(waLink(whatsAppText()), "_blank");
      return;
    }
    if (!token) { toast.info("Sign in to book a Mate"); nav(`/login?next=/services/${slug}`); return; }
    if (!contactName.trim() || !contactPhone.trim()) {
      toast.error("Please add your name and mobile number"); return;
    }
    if (locMode !== "none" && !pickup.address.trim()) {
      toast.error("Please add a pickup / area"); return;
    }
    if (locMode === "route" && !drop.address.trim()) {
      toast.error("Please add a drop area"); return;
    }
    setSubmitting(true);
    try {
      const b = await authFetch({ method: "POST", url: `${API}/bookings`, data: buildPayload() });
      if (payMethod === "upi") {
        setUpiBooking(b.data);
        setSubmitting(false);
        return;
      }
      const c = await authFetch({
        method: "POST", url: `${API}/payments/checkout`,
        data: {
          lookup_key: svc.lookup_key, quantity: perHour ? hours : 1,
          origin_url: window.location.origin, booking_id: b.data.id, kind: "booking",
        },
      });
      window.location.href = c.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24">
      <Link to="/services" data-testid="back-to-services" className="text-sm text-neutral-600 hover:text-[color:var(--primary)] inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All services
      </Link>

      <div className="grid lg:grid-cols-12 gap-10 mt-6">
        {/* LEFT: Service info with broader photo */}
        <div className="lg:col-span-7">
          {svc.photo && (
            <div className="relative rounded-3xl overflow-hidden border border-black/10 mb-8 shadow-lg">
              <img src={svc.photo} alt={svc.name} className="w-full h-72 md:h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">{svc.unit}</div>
                  <div className="font-display text-4xl md:text-5xl">₹{svc.price_inr}</div>
                </div>
                <div className="chip bg-white/90 backdrop-blur border-white/40">Verified · {city?.name || "your city"}</div>
              </div>
            </div>
          )}
          <div className="w-14 h-14 rounded-full grid place-items-center" style={{ background: svc.color, color: "#fff" }}>
            <Icon className="w-6 h-6" />
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold mt-5 leading-tight">{svc.name}</h1>
          <p className="text-lg text-[color:var(--text-muted)] mt-3 max-w-xl">{svc.tagline}</p>
          <p className="text-neutral-700 mt-6 leading-relaxed max-w-xl">{svc.description}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            <span className="chip"><ShieldCheck className="w-3.5 h-3.5" /> ID verified</span>
            <span className="chip"><Clock className="w-3.5 h-3.5" /> Match ~10 min</span>
            {locMode !== "none" && <span className="chip"><MapPin className="w-3.5 h-3.5" /> Doorstep service</span>}
          </div>

          <div className="mt-8">
            <div className="label-eyebrow mb-3">what a Mate does</div>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-lg">
              {svc.examples.map((e) => (
                <li key={e} className="border border-black/10 rounded-2xl px-4 py-3 text-sm bg-white">{e}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT: Booking form */}
        <div className="lg:col-span-5">
          <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-8 sticky top-24">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display text-3xl">₹{svc.price_inr}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{svc.unit}</div>
              </div>
              <span className="chip">Instant book</span>
            </div>

            <div className="mt-6 space-y-4">
              {city && city.status !== "live" && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div><b>{city.name}</b> launches {city.launch}. WhatsApp us to reserve early access.</div>
                </div>
              )}

              {/* Contact — prefilled from account */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500">Your name</Label>
                  <div className="mt-1.5 relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="booking-contact-name" className="pl-9"
                      value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500">Mobile</Label>
                  <div className="mt-1.5 relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="booking-contact-phone" className="pl-9" type="tel"
                      value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+91…" />
                  </div>
                </div>
              </div>

              {/* Location (plain text — no map) */}
              {locMode !== "none" && (
                <SimpleLocationInput
                  label={locMode === "route" ? "Pickup / area" : "Service area"}
                  landmarkLabel="Nearby landmark"
                  placeholder="e.g. Kothrud"
                  landmarkPlaceholder="e.g. Opposite Pune Central Mall"
                  value={pickup}
                  onChange={setPickup}
                  testid="pickup-location"
                />
              )}
              {locMode === "route" && (
                <SimpleLocationInput
                  label="Drop area"
                  landmarkLabel="Drop landmark"
                  placeholder="e.g. Kalyani Nagar"
                  landmarkPlaceholder="e.g. Near Westin Hotel"
                  value={drop}
                  onChange={setDrop}
                  testid="drop-location"
                />
              )}

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="booking-date-btn" className="w-full flex items-center justify-between border border-black/10 rounded-xl px-4 py-3 mt-1.5 bg-white hover:border-[color:var(--primary)] transition-colors">
                      <span>{format(date, "PPP")}</span>
                      <CalendarIcon className="w-4 h-4 text-neutral-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500">Time</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="booking-time-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {perHour && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                      <span>Rent duration</span>
                      <span className="text-[color:var(--secondary)] font-bold">{hours} hr{hours > 1 ? "s" : ""}</span>
                    </Label>
                    <input data-testid="booking-hours-slider" type="range" min="1" max="8" step="1" value={hours}
                      onChange={(e) => setHours(Number(e.target.value))}
                      className="w-full mt-2 accent-[color:var(--secondary)]" />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Notes</Label>
                <Textarea data-testid="booking-notes-input" className="mt-1.5" rows={2}
                  placeholder={svc.booking_prompt || "Anything the Mate should know"}
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              {!svc.quote_only && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500">Payment method</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <button type="button" data-testid="pay-method-upi" onClick={() => setPayMethod("upi")}
                      className={`rounded-xl border-2 px-3 py-3 text-left transition-all ${payMethod === "upi" ? "border-[color:var(--secondary)] bg-orange-50" : "border-black/10 bg-white hover:border-black/20"}`}>
                      <div className="flex items-center gap-2">
                        <QrCode className="w-4 h-4" style={{ color: "var(--secondary)" }} />
                        <span className="font-bold text-sm">UPI / QR</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">Scan · pay · UTR</div>
                    </button>
                    <button type="button" data-testid="pay-method-stripe" onClick={() => setPayMethod("stripe")}
                      className={`rounded-xl border-2 px-3 py-3 text-left transition-all ${payMethod === "stripe" ? "border-[color:var(--primary)] bg-blue-50" : "border-black/10 bg-white hover:border-black/20"}`}>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" style={{ color: "var(--primary)" }} />
                        <span className="font-bold text-sm">Card</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">Stripe · secure</div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-black/10 my-6" />

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span>Service</span><span>₹{svc.price_inr} {perHour ? `× ${hours}` : ""}</span></div>
              <div className="flex justify-between text-neutral-500"><span>Platform fee (33%)</span><span>₹{platformFee}</span></div>
              <div className="flex justify-between text-neutral-500"><span>Partner receives</span><span>₹{partnerEarn}</span></div>
              <div className="border-t border-dashed border-black/10 my-2" />
              <div className="flex justify-between font-semibold text-base"><span>Total</span><span>₹{total}</span></div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-2">
              <button data-testid="book-assistance-btn" className="btn-primary w-full" onClick={handleBook} disabled={submitting}>
                {bookLabel({ submitting, city, svc, method: payMethod })}
              </button>
              <button
                data-testid="whatsapp-book-btn"
                onClick={handleWhatsApp}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-emerald-500 text-emerald-700 font-bold py-3 hover:bg-emerald-50 active:scale-[0.99] transition-all"
              >
                <MessageSquareText className="w-4 h-4" /> WhatsApp us directly
              </button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-3 text-center">
              {payMethod === "upi" ? "Scan HeylpMate UPI QR · admin verifies within minutes" : "Powered by Stripe · Test card 4242 4242 4242 4242"}
            </p>
          </div>
        </div>
      </div>

      {upiBooking && <UpiPaymentModal booking={upiBooking} onClose={() => { setUpiBooking(null); nav("/dashboard"); }} />}
    </div>
  );
}
